import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Link,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import StarRoundedIcon from '@mui/icons-material/StarRounded'
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded'
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded'
import EmailRoundedIcon from '@mui/icons-material/EmailRounded'

const CSV_FIELDS: { key: string; label: string }[] = [
  { key: 'phone', label: 'Mobile / phone' },
  { key: 'emails', label: 'Email' },
  { key: 'title', label: 'Title' },
  { key: 'link', label: 'Airbnb link' },
  { key: 'category', label: 'Category' },
  { key: 'address', label: 'Address' },
  { key: 'website', label: 'Website / Instagram' },
  { key: 'review_count', label: 'Review count' },
  { key: 'review_rating', label: 'Rating' },
  { key: 'reviews_per_rating', label: 'Ratings breakdown' },
  { key: 'latitude', label: 'Latitude' },
  { key: 'longitude', label: 'Longitude' },
  { key: 'status', label: 'Status / price' },
  { key: 'descriptions', label: 'Description' },
  { key: 'timezone', label: 'Timezone' },
  { key: 'price_range', label: 'Price range' },
  { key: 'plus_code', label: 'Plus code' },
  { key: 'data_id', label: 'Listing ID' },
  { key: 'place_id', label: 'Place ID' },
  { key: 'owner', label: 'Owner / host' },
  { key: 'complete_address', label: 'Complete address' },
  { key: 'about', label: 'About' },
  { key: 'user_reviews', label: 'Reviews' },
  { key: 'user_reviews_extended', label: 'More reviews' },
  { key: 'open_hours', label: 'Check-in / out' },
  { key: 'popular_times', label: 'Popular times' },
  { key: 'reviews_link', label: 'Reviews link' },
  { key: 'street_view_url', label: 'Street view' },
  { key: 'reservations', label: 'Reservations' },
  { key: 'order_online', label: 'Order online' },
  { key: 'menu', label: 'Menu' },
  { key: 'credit_cards_accepted', label: 'Cards accepted' },
  { key: 'cid', label: 'CID' },
  { key: 'input_id', label: 'City / input ID' },
]

const CAROUSEL_SKIP = new Set(['images', 'thumbnail'])

function parseJsonish(value: unknown): unknown {
  if (value == null || value === '') return null
  if (typeof value === 'object') return value
  if (typeof value !== 'string') return value
  const t = value.trim()
  if (!t || t === 'null') return null
  if ((t.startsWith('{') && t.endsWith('}')) || (t.startsWith('[') && t.endsWith(']'))) {
    try {
      return JSON.parse(t)
    } catch {
      return value
    }
  }
  return value
}

function isBlank(value: unknown): boolean {
  if (value == null) return true
  if (typeof value === 'string' && value.trim() === '') return true
  if (Array.isArray(value) && value.length === 0) return true
  if (typeof value === 'object' && !Array.isArray(value)) {
    const vals = Object.values(value as Record<string, unknown>)
    return vals.length === 0 || vals.every((v) => v == null || String(v).trim() === '')
  }
  return false
}

function isHttpUrl(s: string) {
  return /^https?:\/\//i.test(s.trim())
}

function imageKey(url: string) {
  return url.split('?')[0]
}

export function extractListingImages(
  csvRow: Record<string, string>,
  extra?: string | null,
): { src: string; title?: string }[] {
  const parsed = parseJsonish(csvRow.images)
  const out: { src: string; title?: string }[] = []
  const seen = new Set<string>()
  const add = (src?: string | null, title?: string) => {
    if (!src || !String(src).trim()) return
    const url = String(src).trim()
    const key = imageKey(url)
    if (seen.has(key) || seen.has(url)) return
    seen.add(key)
    seen.add(url)
    out.push({ src: url, title })
  }
  if (Array.isArray(parsed)) {
    for (const item of parsed) {
      if (typeof item === 'string') add(item)
      else if (item && typeof item === 'object') {
        const o = item as Record<string, unknown>
        add(String(o.image || o.url || o.src || ''), String(o.title || o.caption || ''))
      }
    }
  }
  add(extra)
  add(csvRow.thumbnail)
  return out
}

function splitList(value: unknown): string[] {
  if (value == null || value === '') return []
  if (Array.isArray(value)) return value.map((v) => String(v).trim()).filter(Boolean)
  return String(value)
    .split(/[,;]+/)
    .map((s) => s.trim())
    .filter(Boolean)
}

function igLabel(url: string) {
  const m = url.match(/instagram\.com\/([A-Za-z0-9._]+)/i)
  return m ? `@${m[1]}` : url
}

function EmptyDash() {
  return (
    <Typography variant="body2" color="text.secondary">
      —
    </Typography>
  )
}

function ExternalLink({ href, children }: { href: string; children?: ReactNode }) {
  return (
    <Link href={href} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ wordBreak: 'break-all' }}>
      {children || href}
    </Link>
  )
}

function ObjectGrid({ obj }: { obj: Record<string, unknown> }) {
  const entries = Object.entries(obj).filter(([, v]) => !isBlank(v))
  if (!entries.length) return <EmptyDash />
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        columnGap: 1.25,
        rowGap: 0.5,
      }}
    >
      {entries.map(([k, v]) => (
        <Box key={k} sx={{ display: 'contents' }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize', pt: 0.15 }}>
            {k.replace(/_/g, ' ')}
          </Typography>
          <FieldValue value={v} nested />
        </Box>
      ))}
    </Box>
  )
}

function ReviewCard({ review }: { review: Record<string, unknown> }) {
  const name = String(review.Name || review.name || review.author || review.reviewer || 'Guest')
  const rating = review.Rating ?? review.rating ?? review.stars
  const when = String(review.When || review.when || review.date || review.created_at || '')
  const text = String(
    review.Description || review.description || review.comments || review.text || review.text_original || '',
  )
  const translated = String(review.text_translated || '')
  const photo = String(review.ProfilePicture || review.profile_picture || review.avatar || '')
  const source = String(review.source || '')
  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 2,
        border: '1px solid rgba(20,24,32,0.08)',
        bgcolor: 'rgba(255,255,255,0.7)',
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <Avatar src={photo || undefined} alt="" sx={{ width: 36, height: 36, fontSize: 14 }}>
          {name.slice(0, 1).toUpperCase()}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
            <Typography variant="body2" fontWeight={600}>
              {name}
            </Typography>
            {rating != null && String(rating) !== '' && (
              <Stack direction="row" alignItems="center" spacing={0.25}>
                <StarRoundedIcon sx={{ fontSize: 14, color: '#C9A227' }} />
                <Typography variant="caption" fontWeight={700}>
                  {String(rating)}
                </Typography>
              </Stack>
            )}
            {source && <Chip label={source} size="small" sx={{ height: 18, fontSize: 10 }} />}
          </Stack>
          {when && (
            <Typography variant="caption" color="text.secondary">
              {when}
            </Typography>
          )}
          {text && (
            <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
              {text}
            </Typography>
          )}
          {translated && translated !== text && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {translated}
            </Typography>
          )}
        </Box>
      </Stack>
    </Box>
  )
}

function FieldValue({ value, nested = false }: { value: unknown; nested?: boolean }) {
  const parsed = parseJsonish(value)
  if (isBlank(parsed)) return <EmptyDash />

  if (typeof parsed === 'string') {
    const t = parsed.trim()
    if (isHttpUrl(t)) return <ExternalLink href={t} />
    if (/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(t) || (t.includes('@') && t.includes(',') && t.includes('.'))) {
      const emails = t.split(/[,;\s]+/).filter((e) => e.includes('@'))
      if (emails.length) {
        return (
          <Stack spacing={0.25}>
            {emails.map((e) => (
              <Link key={e} href={`mailto:${e}`} underline="hover">
                {e}
              </Link>
            ))}
          </Stack>
        )
      }
    }
    if (/^[\d+\-\s(),./]{7,}$/.test(t.replace(/\s/g, ' ')) || (t.includes(',') && /[6-9]\d{4}/.test(t))) {
      const phones = splitList(t)
      if (phones.length) {
        return (
          <Stack spacing={0.25}>
            {phones.map((p) => (
              <Link key={p} href={`tel:${p.replace(/[^\d+]/g, '')}`} underline="hover">
                {p}
              </Link>
            ))}
          </Stack>
        )
      }
    }
    if (/instagram\.com/i.test(t)) {
      const urls = splitList(t).filter((u) => /instagram\.com/i.test(u) || u.startsWith('@'))
      if (urls.length) {
        return (
          <Stack spacing={0.25}>
            {urls.map((u) => {
              const href = u.startsWith('http') ? u : `https://www.instagram.com/${u.replace(/^@/, '')}`
              return (
                <ExternalLink key={u} href={href}>
                  {igLabel(href)}
                </ExternalLink>
              )
            })}
          </Stack>
        )
      }
    }
    return (
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {t}
      </Typography>
    )
  }

  if (typeof parsed === 'number' || typeof parsed === 'boolean') {
    return <Typography variant="body2">{String(parsed)}</Typography>
  }

  if (Array.isArray(parsed)) {
    if (parsed.every((x) => typeof x === 'string' || typeof x === 'number')) {
      return (
        <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.75}>
          {parsed.map((item, i) => (
            <Chip key={`${item}-${i}`} label={String(item)} size="small" sx={{ height: 'auto', py: 0.5, '& .MuiChip-label': { whiteSpace: 'normal' } }} />
          ))}
        </Stack>
      )
    }
    const looksLikeReviews = parsed.some(
      (x) => x && typeof x === 'object' && ('Rating' in (x as object) || 'rating' in (x as object) || 'comments' in (x as object)),
    )
    if (looksLikeReviews) {
      return (
        <Stack spacing={1}>
          {(parsed as Record<string, unknown>[]).map((r, i) => (
            <ReviewCard key={i} review={r} />
          ))}
        </Stack>
      )
    }
    return (
      <Stack spacing={1}>
        {parsed.map((item, i) => (
          <Box key={i} sx={{ pl: nested ? 0 : 0.5 }}>
            <FieldValue value={item} nested />
          </Box>
        ))}
      </Stack>
    )
  }

  if (typeof parsed === 'object') {
    const obj = parsed as Record<string, unknown>
    const keys = Object.keys(obj)
    const addressKeys = new Set(['street', 'city', 'state', 'postal_code', 'country', 'borough'])
    if (keys.length && keys.every((k) => addressKeys.has(k))) {
      const line = [obj.street, obj.borough, obj.city, [obj.state, obj.postal_code].filter((x) => !isBlank(x)).join(' '), obj.country]
        .map((x) => String(x || '').trim())
        .filter(Boolean)
        .join(', ')
      return line ? (
        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
          {line}
        </Typography>
      ) : (
        <EmptyDash />
      )
    }
    if (obj.name || obj.link) {
      return (
        <Box>
          {obj.name ? (
            <Typography variant="body2" fontWeight={600}>
              {String(obj.name)}
            </Typography>
          ) : null}
          {obj.link && isHttpUrl(String(obj.link)) ? <ExternalLink href={String(obj.link)}>{String(obj.link)}</ExternalLink> : null}
          {obj.id ? (
            <Typography variant="caption" color="text.secondary" display="block">
              ID {String(obj.id)}
            </Typography>
          ) : null}
        </Box>
      )
    }
    return <ObjectGrid obj={obj} />
  }

  return (
    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
      {String(parsed)}
    </Typography>
  )
}

function ListingImageCarousel({ images }: { images: { src: string; title?: string }[] }) {
  const [index, setIndex] = useState(0)
  const [height, setHeight] = useState<number>(240)
  const wrapRef = useRef<HTMLDivElement>(null)
  const startX = useRef<number | null>(null)

  useEffect(() => {
    setIndex(0)
  }, [images])

  const applyHeight = (img: HTMLImageElement) => {
    const w = wrapRef.current?.clientWidth || img.clientWidth
    if (!img.naturalWidth || !w) return
    setHeight(Math.max(160, (img.naturalHeight / img.naturalWidth) * w))
  }

  const go = (dir: number) => {
    setIndex((i) => (i + dir + images.length) % images.length)
  }

  if (!images.length) return null
  const current = images[index]

  return (
    <Box className="admin-listing-carousel" ref={wrapRef}>
      <Box sx={{ height, transition: 'height 0.28s ease', position: 'relative', bgcolor: '#0c0f14', overflow: 'hidden' }}>
        <Box
          component="img"
          key={current.src}
          src={current.src}
          alt={current.title || `Photo ${index + 1}`}
          onLoad={(e) => applyHeight(e.currentTarget)}
          onTouchStart={(e) => {
            startX.current = e.changedTouches[0]?.clientX ?? null
          }}
          onTouchEnd={(e) => {
            if (startX.current == null) return
            const dx = (e.changedTouches[0]?.clientX ?? startX.current) - startX.current
            if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1)
            startX.current = null
          }}
          sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', verticalAlign: 'middle' }}
        />
        {images.length > 1 && (
          <>
            <IconButton className="admin-listing-carousel-nav admin-listing-carousel-nav--left" onClick={() => go(-1)} aria-label="Previous photo">
              <ChevronLeftRoundedIcon />
            </IconButton>
            <IconButton className="admin-listing-carousel-nav admin-listing-carousel-nav--right" onClick={() => go(1)} aria-label="Next photo">
              <ChevronRightRoundedIcon />
            </IconButton>
            <Box className="admin-listing-carousel-count">
              {index + 1} / {images.length}
            </Box>
          </>
        )}
      </Box>
      {images.length > 1 && (
        <Box className="admin-listing-carousel-thumbs">
          {images.map((img, i) => (
            <Box
              key={`${img.src}-${i}`}
              component="button"
              type="button"
              onClick={() => setIndex(i)}
              className={`admin-listing-carousel-thumb${i === index ? ' is-active' : ''}`}
              aria-label={`Photo ${i + 1}`}
            >
              <Box component="img" src={img.src} alt="" />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

function statusChip(status: string) {
  const map: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
    done: 'success',
    completed: 'success',
    skipped: 'info',
    running: 'warning',
    scraping: 'warning',
    failed: 'error',
    partial: 'warning',
    none: 'default',
    pending: 'default',
  }
  return <Chip label={status} size="small" color={map[status] || 'default'} sx={{ height: 22, fontSize: 11 }} />
}

type ListingDetailDrawerProps = {
  open: boolean
  isMobile: boolean
  loading: boolean
  listingId: string | null
  detail: Record<string, unknown> | null
  onClose: () => void
}

export default function ListingDetailDrawer({
  open,
  isMobile,
  loading,
  listingId,
  detail,
  onClose,
}: ListingDetailDrawerProps) {
  const csvRow = useMemo(() => (detail?.csv_row || {}) as Record<string, string>, [detail])
  const images = useMemo(
    () => extractListingImages(csvRow, (detail?.thumbnail as string) || null),
    [csvRow, detail],
  )
  const contacts = useMemo(() => {
    const raw = (detail?.contacts || {}) as {
      phones?: string[]
      emails?: string[]
      instagrams?: string[]
      sources?: string[]
      evidence?: unknown[]
    }
    return {
      phones: raw.phones?.length ? raw.phones : splitList(detail?.phone || csvRow.phone),
      emails: raw.emails?.length ? raw.emails : splitList(detail?.email || csvRow.emails),
      instagrams: raw.instagrams?.length ? raw.instagrams : splitList(detail?.instagram_url),
      sources: raw.sources || [],
      evidence: raw.evidence || [],
    }
  }, [detail, csvRow])

  const title = (detail?.title as string) || csvRow.title || listingId || 'Listing'
  const lat = csvRow.latitude || String(detail?.latitude || '')
  const lng = csvRow.longitude || String(detail?.longitude || '')
  const mapsHref = lat && lng ? `https://www.google.com/maps?q=${encodeURIComponent(`${lat},${lng}`)}` : ''

  return (
    <Drawer
      anchor={isMobile ? 'bottom' : 'right'}
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          className: 'admin-listing-drawer',
          sx: {
            width: { xs: '100%', sm: 520, md: 560 },
            maxHeight: isMobile ? '94dvh' : '100%',
            borderTopLeftRadius: isMobile ? 16 : 0,
            borderTopRightRadius: isMobile ? 16 : 0,
          },
        },
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <IconButton className="admin-listing-drawer-close" onClick={onClose} aria-label="close" size="small">
          <CloseRoundedIcon />
        </IconButton>

        {loading && !detail ? (
          <Skeleton variant="rectangular" height={240} animation="wave" />
        ) : images.length ? (
          <ListingImageCarousel images={images} />
        ) : (
          <Box sx={{ height: 48, bgcolor: 'transparent' }} />
        )}
      </Box>

      <Box sx={{ p: { xs: 2, sm: 2.5 }, overflow: 'auto', pb: 5 }}>
        {loading && !detail ? (
          <Stack spacing={1.5}>
            <Skeleton width="80%" height={32} />
            <Skeleton height={88} />
            <Skeleton height={160} />
          </Stack>
        ) : (
          <>
            <Typography variant="h6" fontWeight={700} sx={{ pr: 4, mb: 0.5, lineHeight: 1.3 }}>
              {title}
            </Typography>
            {(csvRow.address || detail?.location_text) && (
              <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ mb: 1.5 }}>
                <PlaceRoundedIcon sx={{ fontSize: 16, mt: 0.25, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {String(csvRow.address || detail?.location_text)}
                </Typography>
              </Stack>
            )}

            <Box className="admin-listing-highlights">
              {[
                ['Mobile', contacts.phones.join(', '), 'phone'],
                ['Email', contacts.emails.join(', '), 'email'],
                ['Instagram', contacts.instagrams.join(', '), 'url'],
                ['Host', detail?.host_name, 'text'],
                ['Scrape', detail?.scrape_status, 'status'],
                ['Enrichment', detail?.enrichment_status, 'status'],
              ].map(([label, val, kind]) => (
                <Box key={String(label)}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    {label}
                  </Typography>
                  {kind === 'status' && val ? (
                    <Box sx={{ mt: 0.25 }}>{statusChip(String(val))}</Box>
                  ) : kind === 'phone' && val ? (
                    <Stack direction="row" spacing={0.5} alignItems="flex-start">
                      <PhoneRoundedIcon sx={{ fontSize: 14, mt: 0.35, color: 'text.secondary' }} />
                      <FieldValue value={val} />
                    </Stack>
                  ) : kind === 'email' && val ? (
                    <Stack direction="row" spacing={0.5} alignItems="flex-start">
                      <EmailRoundedIcon sx={{ fontSize: 14, mt: 0.35, color: 'text.secondary' }} />
                      <FieldValue value={val} />
                    </Stack>
                  ) : (
                    <FieldValue value={val} />
                  )}
                </Box>
              ))}
            </Box>

            {contacts.sources.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                  Contact sources
                </Typography>
                <Stack spacing={0.35}>
                  {contacts.sources.slice(0, 12).map((src) => (
                    <Typography key={src} variant="caption" sx={{ wordBreak: 'break-all', display: 'block' }}>
                      {src}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )}

            {(detail?.listing_url || csvRow.link || mapsHref) && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2.5 }}>
                {(detail?.listing_url || csvRow.link) && (
                  <Button
                    href={String(detail?.listing_url || csvRow.link)}
                    target="_blank"
                    rel="noopener"
                    startIcon={<OpenInNewRoundedIcon />}
                    size="small"
                    variant="outlined"
                  >
                    Open listing
                  </Button>
                )}
                {mapsHref && (
                  <Button href={mapsHref} target="_blank" rel="noopener" startIcon={<PlaceRoundedIcon />} size="small">
                    Map
                  </Button>
                )}
              </Stack>
            )}

            {loading && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <CircularProgress size={16} />
                <Typography variant="caption">Refreshing details…</Typography>
              </Stack>
            )}

            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.25 }}>
              All CSV columns
            </Typography>
            <Stack spacing={1.5}>
              {CSV_FIELDS.filter((f) => !CAROUSEL_SKIP.has(f.key)).map(({ key, label }) => (
                <Box key={key} sx={{ pb: 1.25, borderBottom: '1px solid rgba(20,24,32,0.06)' }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.35 }}>
                    {label}
                  </Typography>
                  <FieldValue value={csvRow[key]} />
                </Box>
              ))}
            </Stack>
          </>
        )}
      </Box>
    </Drawer>
  )
}
